import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Pending from "@/pages/Pending";
import Rejected from "@/pages/Rejected";
import AuthCallback from "@/components/AuthCallback";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminCourses from "@/pages/AdminCourses";
import TeacherDashboard from "@/pages/TeacherDashboard";
import StudentDashboard from "@/pages/StudentDashboard";
import NewCourseWizard from "@/pages/NewCourseWizard";
import CourseEditor from "@/pages/CourseEditor";
import PublicCatalog from "@/pages/PublicCatalog";
import PublicCourseDetail from "@/pages/PublicCourseDetail";
import PocAI from "@/pages/PocAI";
import CalendarPage from "@/pages/CalendarPage";
import LiveSession from "@/pages/LiveSession";
import AdminSessions from "@/pages/AdminSessions";
import AdminEnrollments from "@/pages/AdminEnrollments";
import AdminReferrals from "@/pages/AdminReferrals";
import TeacherStudents from "@/pages/TeacherStudents";
import StudentCourseView from "@/pages/StudentCourseView";
import AdminPaymentSettings from "@/pages/AdminPaymentSettings";
import PaymentReturn from "@/pages/PaymentReturn";
import TestRunner from "@/pages/TestRunner";
import TeacherGrading from "@/pages/TeacherGrading";
import AdminGrading from "@/pages/AdminGrading";
import StudentLearnHub from "@/pages/StudentLearnHub";
import StudentLearnNew from "@/pages/StudentLearnNew";
import StudentLearnCourse from "@/pages/StudentLearnCourse";
import ParentInvite from "@/pages/ParentInvite";
import ParentDashboard from "@/pages/ParentDashboard";
import RewardAnimator from "@/components/RewardAnimator";

function ProtectedAny({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <RewardAnimator />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          {/* Phase 20 — public parent invitation landing */}
          <Route path="/parent-invite" element={<ParentInvite />} />
          <Route
            path="/pending-approval"
            element={
              <ProtectedAny>
                <Pending />
              </ProtectedAny>
            }
          />
          <Route
            path="/rejected"
            element={
              <ProtectedAny>
                <Rejected />
              </ProtectedAny>
            }
          />

          {/* Admin */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute roles={["admin"]}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/courses"
            element={
              <ProtectedRoute roles={["admin"]}>
                <AdminCourses />
              </ProtectedRoute>
            }
          />

          {/* Teacher */}
          <Route
            path="/teacher"
            element={
              <ProtectedRoute roles={["teacher"]}>
                <TeacherDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/courses/new"
            element={
              <ProtectedRoute roles={["teacher", "admin"]}>
                <NewCourseWizard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/courses/blank"
            element={
              <ProtectedRoute roles={["teacher", "admin"]}>
                <NewCourseWizard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/courses/:id/edit"
            element={
              <ProtectedRoute roles={["teacher", "admin"]}>
                <CourseEditor />
              </ProtectedRoute>
            }
          />

          {/* Student */}
          <Route
            path="/student"
            element={
              <ProtectedRoute roles={["student"]}>
                <StudentDashboard />
              </ProtectedRoute>
            }
          />

          {/* Catalog — any logged-in user */}
          <Route
            path="/courses"
            element={
              <ProtectedAny>
                <PublicCatalog />
              </ProtectedAny>
            }
          />
          <Route
            path="/courses/:id"
            element={<PublicCourseDetail />}
          />

          {/* Calendar & Live class — any approved user */}
          <Route
            path="/calendar"
            element={
              <ProtectedAny>
                <CalendarPage />
              </ProtectedAny>
            }
          />
          <Route
            path="/sessions/:id/live"
            element={
              <ProtectedAny>
                <LiveSession />
              </ProtectedAny>
            }
          />

          {/* Admin sessions list */}
          <Route
            path="/admin/sessions"
            element={
              <ProtectedRoute roles={["admin"]}>
                <AdminSessions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/enrollments"
            element={
              <ProtectedRoute roles={["admin"]}>
                <AdminEnrollments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/payment-settings"
            element={
              <ProtectedRoute roles={["admin"]}>
                <AdminPaymentSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/referrals"
            element={
              <ProtectedRoute roles={["admin"]}>
                <AdminReferrals />
              </ProtectedRoute>
            }
          />

          {/* Payment return (Stripe) */}
          <Route
            path="/payment/return"
            element={
              <ProtectedAny>
                <PaymentReturn />
              </ProtectedAny>
            }
          />

          {/* Phase 6 — Grading & Tests */}
          <Route
            path="/student/test-attempts/:attemptId"
            element={
              <ProtectedRoute roles={["student"]}>
                <TestRunner />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/grading"
            element={
              <ProtectedRoute roles={["teacher"]}>
                <TeacherGrading />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/grading"
            element={
              <ProtectedRoute roles={["admin"]}>
                <AdminGrading />
              </ProtectedRoute>
            }
          />

          {/* Teacher students */}
          <Route
            path="/teacher/students"
            element={
              <ProtectedRoute roles={["teacher"]}>
                <TeacherStudents />
              </ProtectedRoute>
            }
          />

          {/* Student course view */}
          <Route
            path="/student/courses/:id"
            element={
              <ProtectedRoute roles={["student"]}>
                <StudentCourseView />
              </ProtectedRoute>
            }
          />

          {/* Phase 15b — Self-paced learning paths */}
          <Route
            path="/student/learn"
            element={
              <ProtectedRoute roles={["student"]}>
                <StudentLearnHub />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student/learn/new"
            element={
              <ProtectedRoute roles={["student"]}>
                <StudentLearnNew />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student/learn/:courseId"
            element={
              <ProtectedRoute roles={["student"]}>
                <StudentLearnCourse />
              </ProtectedRoute>
            }
          />

          {/* Phase 20 — Parent dashboard */}
          <Route
            path="/parent"
            element={
              <ProtectedRoute roles={["parent"]}>
                <ParentDashboard />
              </ProtectedRoute>
            }
          />

          {/* POC stays public */}
          <Route path="/poc/ai" element={<PocAI />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
